// src/components/games/diceMatch/DiceMatch.tsx
import { logger } from '../../../utils/logger';

import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
  IonCard,
  IonCardContent,
  IonBadge,
  IonGrid,
  IonRow,
  IonCol,
  IonAlert,
  IonBackButton,
  IonButtons,
} from '@ionic/react';
import { close, trophy, helpCircle, refresh, arrowUndo, dice } from 'ionicons/icons';
import {
  GameState,
  DiceType,
  DiceSlot,
  DEFAULT_CONFIG,
  GameResult,
} from './types';
import {
  createInitialGameState,
  placeDie,
  getNewDie,
  purchaseUpgrade,
  calculateMetaCurrency,
  saveStateSnapshot,
  undoLastMove,
  regenerateDiceFromSlots,
} from './gameLogic';
import {
  submitGameScore,
  getPersonalBest,
  getLeaderboard,
  getMetaProgression,
  DiceMatchScore,
  MetaProgression,
} from '../../../services/diceMatchService';
import {
  getUserTrinkets,
  consumeTrinket,
  rollRandomTrinket,
  awardTrinket,
  getTrinketTemplates,
} from '../../../services/trinketService';
import { UserTrinket, TrinketTemplate } from './trinketTypes';
import { trackGameStart, trackGameComplete, trackGameQuit } from '../../../services/analyticsService';
import { soundService } from '../../../services/soundService';
import './DiceMatch.css';

const DICE_EMOJI: Record<DiceType, string> = {
  'd4': '🔺',
  'd6': '🎲',
  'd8': '💎',
  'd10': '🔟',
  'd12': '⬡',
  'd20': '🌟',
  'd100': '💯',
  'sparkle': '✨',
};

/**
 * Apply meta progression upgrades to the game config
 */
function applyMetaUpgrades(baseConfig: typeof DEFAULT_CONFIG, meta: MetaProgression | null): typeof DEFAULT_CONFIG {
  if (!meta) return baseConfig;

  // Currency multiplier: 1.0 + (level * 0.1) = 1.0, 1.1, 1.2, ..., 2.0
  const currencyMultiplier = 1.0 + (meta.upgrade_currency_multiplier * 0.1);

  return {
    ...baseConfig,
    startingSlots: baseConfig.startingSlots + meta.upgrade_starting_dice,
    gridSize: baseConfig.gridSize + (meta.upgrade_starting_grid > 0 ? 1 : 0),
    currencyMultiplier,
  };
}

const DiceMatch: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>(() =>
    createInitialGameState(DEFAULT_CONFIG)
  );
  const [showGameOver, setShowGameOver] = useState(false);
  const [showUpgradeAlert, setShowUpgradeAlert] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState<'slot' | null>(null);
  const [personalBest, setPersonalBest] = useState<number>(0);
  const [leaderboard, setLeaderboard] = useState<DiceMatchScore[]>([]);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [meta, setMeta] = useState<MetaProgression | null>(null);
  const [gameInitialized, setGameInitialized] = useState(false);
  const [equippedTrinkets, setEquippedTrinkets] = useState<UserTrinket[]>([]);
  const [activeTrinkets, setActiveTrinkets] = useState<Set<string>>(new Set());
  const [analyticsSessionId, setAnalyticsSessionId] = useState<string>('');
  const [droppedTrinket, setDroppedTrinket] = useState<TrinketTemplate | null>(null);
  const [showWildDieSelector, setShowWildDieSelector] = useState(false);
  const [pendingPlacement, setPendingPlacement] = useState<{row: number, col: number} | null>(null);
  const [showEndGameConfirm, setShowEndGameConfirm] = useState(false);
  const [initialMoves, setInitialMoves] = useState<number>(DEFAULT_CONFIG.startingMoves);
  const [scoreSubmissionError, setScoreSubmissionError] = useState(false);
  const [isSavingScore, setIsSavingScore] = useState(false);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(true);

  // Load meta progression, personal best, and leaderboard on mount
  useEffect(() => {
    const loadData = async () => {
      // Load sound effects
      soundService.loadSounds({
        'dice-place': '/assets/sounds/dice-match/place.mp3',
        'dice-merge': '/assets/sounds/dice-match/merge.mp3',
        'dice-sparkle': '/assets/sounds/dice-match/sparkle.mp3',
        'dice-gameover': '/assets/sounds/dice-match/gameover.mp3',
      });

      setIsLoadingLeaderboard(true);
      const [metaData, best, leaders, trinkets] = await Promise.all([
        getMetaProgression(),
        getPersonalBest(),
        getLeaderboard(10),
        getUserTrinkets(),
      ]);

      setMeta(metaData);
      setPersonalBest(best);
      setLeaderboard(leaders);
      setIsLoadingLeaderboard(false);

      // Filter to equipped trinkets + all temporary (one-time) trinkets
      const equipped = trinkets.filter(t =>
        t.template && (
          t.is_equipped ||  // Include equipped trinkets (permanent/per-game)
          t.template.duration === 'temporary'  // Include all temporary trinkets
        )
      );
      setEquippedTrinkets(equipped);

      // Initialize game with meta upgrades applied
      const config = applyMetaUpgrades(DEFAULT_CONFIG, metaData);
      const initialState = createInitialGameState(config);

      // Store initial moves for calculating moves used
      setInitialMoves(config.startingMoves);

      // Apply permanent trinket bonuses to initial state
      let stateWithTrinkets = initialState;
      equipped.forEach(trinket => {
        if (trinket.template?.duration === 'permanent') {
          stateWithTrinkets = applyTrinketEffectToState(stateWithTrinkets, trinket);
        }
      });

      setGameState(stateWithTrinkets);
      setGameInitialized(true);

      // Track game start
      const sessionId = trackGameStart('dice-match', {
        startingMoves: config.startingMoves,
        startingSlots: config.startingSlots,
        gridSize: config.gridSize,
        equippedTrinkets: equipped.length,
      });
      setAnalyticsSessionId(sessionId);

      // Check for and retry any failed score submissions
      retryFailedScores();
    };
    loadData();
  }, []);

  // Retry failed score submissions
  const retryFailedScores = async () => {
    try {
      const failedSubmissions = JSON.parse(localStorage.getItem('diceMatch_failedScores') || '[]');

      if (failedSubmissions.length === 0) return;

      logger.log(`Retrying ${failedSubmissions.length} failed score submission(s)...`);

      const remaining = [];
      for (const submission of failedSubmissions) {
        // Only retry submissions less than 24 hours old
        if (Date.now() - submission.timestamp < 24 * 60 * 60 * 1000) {
          const success = await submitGameScore(submission.result);
          if (!success) {
            // Still failed, keep for next retry
            remaining.push(submission);
          } else {
            logger.log('Successfully retried score submission');
          }
        }
      }

      // Update localStorage with remaining failed submissions
      localStorage.setItem('diceMatch_failedScores', JSON.stringify(remaining));
    } catch (error) {
      logger.error('Error retrying failed scores:', error);
    }
  };

  // Auto-roll first die after game is initialized
  useEffect(() => {
    if (gameInitialized && gameState.currentRoll === null) {
      setGameState(getNewDie(gameState));
    }
  }, [gameInitialized]);

  // Track game quit on component unmount (if game is still active)
  useEffect(() => {
    return () => {
      // Only track quit if game was active and not completed/saved
      if (gameInitialized && gameState.gameStatus === 'active' && !scoreSaved) {
        trackGameQuit('dice-match', {
          score: gameState.score,
          movesRemaining: gameState.movesRemaining,
          sparklesCreated: gameState.sparklesCreated,
        });
      }
    };
  }, [gameInitialized, gameState.gameStatus, gameState.score, gameState.movesRemaining, gameState.sparklesCreated, scoreSaved]);

  // Check for game over and submit score
  useEffect(() => {
    if (gameState.gameStatus !== 'active' && !scoreSaved) {
      setShowGameOver(true);

      // Play game over sound
      soundService.play('dice-gameover', { volume: 0.6, haptic: 'medium' });

      // Get highest die from grid
      let highestDie: DiceType = 'd4';
      for (const row of gameState.grid) {
        for (const cell of row) {
          if (cell.diceType && cell.diceType !== null) {
            const diceOrder = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20', 'd100', 'sparkle'];
            const currentIndex = diceOrder.indexOf(cell.diceType);
            const highestIndex = diceOrder.indexOf(highestDie);
            if (currentIndex > highestIndex) {
              highestDie = cell.diceType;
            }
          }
        }
      }

      // Submit score
      const result: GameResult = {
        score: gameState.score,
        highestDie,
        sparklesCreated: gameState.sparklesCreated,
        movesUsed: initialMoves - gameState.movesRemaining,
        metaCurrencyEarned: calculateMetaCurrency(gameState.sparklesCreated),
      };

      // Store result for potential retry
      const pendingResult = result;

      setIsSavingScore(true);
      submitGameScore(result).then(async (success) => {
        setIsSavingScore(false);

        if (success) {
          setScoreSaved(true);
          setScoreSubmissionError(false);

          // Track game completion
          trackGameComplete('dice-match', result.score, undefined, {
            highestDie,
            sparklesCreated: result.sparklesCreated,
            movesUsed: result.movesUsed,
            metaCurrencyEarned: result.metaCurrencyEarned,
          });

          // Reload personal best and leaderboard
          getPersonalBest().then(setPersonalBest);
          getLeaderboard(10).then(setLeaderboard);

          // Roll for trinket drop (10% chance base, increased by score/sparkles)
          const dropChance = Math.min(0.1 + (gameState.sparklesCreated * 0.05), 0.5); // 10% base, +5% per sparkle, max 50%
          if (Math.random() < dropChance) {
            const trinketId = await rollRandomTrinket();
            if (trinketId) {
              const awarded = await awardTrinket(trinketId);
              if (awarded) {
                // Load trinket template to show user
                const templates = await getTrinketTemplates();
                const template = templates.find(t => t.id === trinketId);
                if (template) {
                  setDroppedTrinket(template);
                }
              }
            }
          }
        } else {
          // Score submission failed
          setScoreSubmissionError(true);

          // Store failed submission in localStorage for potential retry
          try {
            const failedSubmissions = JSON.parse(localStorage.getItem('diceMatch_failedScores') || '[]');
            failedSubmissions.push({
              result: pendingResult,
              timestamp: Date.now(),
            });
            // Keep only last 5 failed submissions
            localStorage.setItem('diceMatch_failedScores', JSON.stringify(failedSubmissions.slice(-5)));
          } catch (error) {
            logger.error('Failed to store score for retry:', error);
          }
        }
      }).catch((error) => {
        setIsSavingScore(false);
        setScoreSubmissionError(true);
        logger.error('Score submission error:', error);
      });
    }
  }, [gameState.gameStatus, scoreSaved]);

  const handleCellClick = (row: number, col: number) => {
    if (!gameState.currentRoll || gameState.gameStatus !== 'active') {
      return;
    }

    // If wild die is active, show selector instead of placing
    if (gameState.trinketEffects.wildDieNext) {
      setPendingPlacement({ row, col });
      setShowWildDieSelector(true);
      return;
    }

    // Save state for undo
    const stateToPlace = saveStateSnapshot(gameState);

    const dieToPlace = gameState.currentRoll;

    // Count dice before placement to detect merges
    const diceCountBefore = stateToPlace.grid.flat().filter(cell => cell.diceType !== null).length;

    // Place the die
    const newState = placeDie(stateToPlace, row, col, dieToPlace);
    if (newState) {
      // Play placement sound with haptic feedback
      soundService.play('dice-place', { volume: 0.5, haptic: 'light' });

      // Count dice after placement
      const diceCountAfter = newState.grid.flat().filter(cell => cell.diceType !== null).length;

      // If dice count decreased, a merge occurred (2 dice became 1)
      const mergeOccurred = diceCountAfter < diceCountBefore + 1; // +1 for the newly placed die
      if (mergeOccurred) {
        // Play merge sound with slight delay so it doesn't overlap with placement sound
        setTimeout(() => {
          soundService.play('dice-merge', { volume: 0.6, haptic: 'medium' });
        }, 150);
      }

      // Check if sparkle was created
      const sparkleCreated = newState.sparklesCreated > stateToPlace.sparklesCreated;
      if (sparkleCreated) {
        soundService.play('dice-sparkle', { volume: 0.7, haptic: 'heavy' });
      }

      // Handle double die effect
      if (gameState.trinketEffects.doubleDieNext && newState.currentRoll === null) {
        // Place another die of the same type in a random empty cell
        const emptyCells: Array<{row: number, col: number}> = [];
        for (let r = 0; r < newState.grid.length; r++) {
          for (let c = 0; c < newState.grid[r].length; c++) {
            if (!newState.grid[r][c].diceType && !newState.grid[r][c].isLocked) {
              emptyCells.push({ row: r, col: c });
            }
          }
        }

        if (emptyCells.length > 0) {
          const randomCell = emptyCells[Math.floor(Math.random() * emptyCells.length)];
          const stateWithDouble = placeDie({...newState, currentRoll: dieToPlace}, randomCell.row, randomCell.col, dieToPlace);
          if (stateWithDouble) {
            // Disable double die effect after use
            stateWithDouble.trinketEffects.doubleDieNext = false;
            setGameState(stateWithDouble);

            setTimeout(() => {
              if (stateWithDouble.movesRemaining > 0) {
                setGameState(getNewDie(stateWithDouble));
              }
            }, 500);
            return;
          }
        }

        // If no empty cells, just disable the effect
        newState.trinketEffects.doubleDieNext = false;
      }

      setGameState(newState);

      // Auto-roll next die if available
      setTimeout(() => {
        if (newState.movesRemaining > 0) {
          setGameState(getNewDie(newState));
        }
      }, 500);
    }
  };

  const handleWildDieSelect = (diceType: DiceType) => {
    if (!pendingPlacement) return;

    // Save state for undo
    const stateToPlace = saveStateSnapshot(gameState);

    // Disable wild die effect
    stateToPlace.trinketEffects.wildDieNext = false;

    // Place the selected die type
    const newState = placeDie(stateToPlace, pendingPlacement.row, pendingPlacement.col, diceType);
    if (newState) {
      setGameState(newState);

      // Auto-roll next die if available
      setTimeout(() => {
        if (newState.movesRemaining > 0) {
          setGameState(getNewDie(newState));
        }
      }, 500);
    }

    setShowWildDieSelector(false);
    setPendingPlacement(null);
  };

  const handleReroll = () => {
    if (gameState.trinketEffects.rerollsRemaining <= 0) return;

    // Decrement reroll count and regenerate dice
    const stateAfterReroll = {
      ...gameState,
      trinketEffects: {
        ...gameState.trinketEffects,
        rerollsRemaining: gameState.trinketEffects.rerollsRemaining - 1,
      },
      availableDice: regenerateDiceFromSlots(gameState.diceSlots),
    };

    setGameState(getNewDie(stateAfterReroll));
  };

  const handleUndo = () => {
    if (gameState.trinketEffects.undoRemaining <= 0 || gameState.moveHistory.length === 0) return;

    const previousState = undoLastMove(gameState);
    if (previousState) {
      setGameState(previousState);
    }
  };

  const handleEndGame = () => {
    setShowEndGameConfirm(true);
  };

  const confirmEndGame = () => {
    setGameState({ ...gameState, gameStatus: 'lost' });
    setShowEndGameConfirm(false);
  };

  const handlePurchaseUpgrade = (type: 'slot') => {
    setSelectedUpgrade(type);
    setShowUpgradeAlert(true);
  };

  const confirmPurchase = () => {
    if (!selectedUpgrade) return;

    const cost = 100;
    const value = 1;

    const newState = purchaseUpgrade(gameState, selectedUpgrade, value, cost);
    if (newState) {
      setGameState(newState);
    }
    setShowUpgradeAlert(false);
    setSelectedUpgrade(null);
  };

  const handleNewGame = () => {
    const config = applyMetaUpgrades(DEFAULT_CONFIG, meta);
    setGameState(createInitialGameState(config));
    setShowGameOver(false);
    setScoreSaved(false);
    setActiveTrinkets(new Set());
    setDroppedTrinket(null);
    setTimeout(() => {
      setGameState(prevState => getNewDie(prevState));
    }, 100);
  };

  const handleActivateTrinket = async (trinket: UserTrinket) => {
    if (!trinket.template) return;

    // Temporary trinkets get consumed
    if (trinket.template.duration === 'temporary') {
      const success = await consumeTrinket(trinket.trinket_id);
      if (success) {
        // Reload equipped trinkets + temporary trinkets
        const trinkets = await getUserTrinkets();
        const equipped = trinkets.filter(t =>
          t.template && (
            t.is_equipped ||  // Include equipped trinkets (permanent/per-game)
            t.template.duration === 'temporary'  // Include all temporary trinkets
          )
        );
        setEquippedTrinkets(equipped);

        // Mark as active for this game
        setActiveTrinkets(prev => new Set(prev).add(trinket.trinket_id));

        // Apply trinket effect immediately
        applyTrinketEffect(trinket);
      }
    } else {
      // Per-game trinkets just activate for this game
      setActiveTrinkets(prev => new Set(prev).add(trinket.trinket_id));
      applyTrinketEffect(trinket);
    }
  };

  // Helper function to apply trinket effect to a state (returns new state)
  const applyTrinketEffectToState = (state: GameState, trinket: UserTrinket): GameState => {
    if (!trinket.template) return state;

    const effect = trinket.template.effect_type;
    const data = trinket.template.effect_data || {};
    const newState = { ...state };

    switch (effect) {
      case 'extra_moves':
        newState.movesRemaining += data.moves || 0;
        break;
      case 'extra_dice': {
        // Cap at 18 slots - upgrade instead of add after that
        const MAX_SLOTS = 18;
        const currentSlotCount = newState.diceSlots.length;
        const numNewSlots = data.dice || 0;

        if (currentSlotCount >= MAX_SLOTS) {
          // At max slots - upgrade existing slots instead
          newState.diceSlots = newState.diceSlots.map(slot => ({
            slotNumber: slot.slotNumber + numNewSlots,
          }));
        } else {
          // Below max - add new slots with sequential slot numbers
          const slotsToAdd = Math.min(numNewSlots, MAX_SLOTS - currentSlotCount);
          const currentMaxSlot = currentSlotCount;
          const newSlots: DiceSlot[] = [];
          for (let i = 1; i <= slotsToAdd; i++) {
            newSlots.push({ slotNumber: currentMaxSlot + i });
          }
          newState.diceSlots = [...newState.diceSlots, ...newSlots];
        }
        // Regenerate dice from all slots (with new numbers/additions)
        newState.availableDice = regenerateDiceFromSlots(newState.diceSlots);
        break;
      }
      case 'score_multiplier':
        newState.trinketEffects = {
          ...newState.trinketEffects,
          scoreMultiplier: newState.trinketEffects.scoreMultiplier * (data.multiplier || 1),
        };
        break;
      case 'currency_multiplier':
        newState.currencyMultiplier *= data.multiplier || 1;
        break;
      case 'reroll':
        newState.trinketEffects = {
          ...newState.trinketEffects,
          rerollsRemaining: newState.trinketEffects.rerollsRemaining + (data.uses || 0),
        };
        break;
      case 'undo_move':
        newState.trinketEffects = {
          ...newState.trinketEffects,
          undoRemaining: newState.trinketEffects.undoRemaining + (data.uses || 0),
        };
        break;
      case 'guarantee_merge':
        newState.trinketEffects = {
          ...newState.trinketEffects,
          guaranteedMerges: newState.trinketEffects.guaranteedMerges + (data.merges || 0),
        };
        break;
      case 'skip_die_level':
        newState.trinketEffects = {
          ...newState.trinketEffects,
          skipDieLevel: true,
        };
        break;
      case 'wild_die':
        newState.trinketEffects = {
          ...newState.trinketEffects,
          wildDieNext: true,
        };
        break;
      case 'double_next_die':
        newState.trinketEffects = {
          ...newState.trinketEffects,
          doubleDieNext: true,
        };
        break;
      default:
        break;
    }

    return newState;
  };

  const applyTrinketEffect = (trinket: UserTrinket) => {
    setGameState(prevState => applyTrinketEffectToState(prevState, trinket));
  };

  const getCellClassName = (row: number, col: number): string => {
    const cell = gameState.grid[row][col];
    const classes = ['dice-cell'];

    if (cell.diceType) {
      classes.push('has-dice');
      classes.push(`dice-${cell.diceType}`);
    }

    if (cell.isLocked) {
      classes.push('locked');
    }

    return classes.join(' ');
  };

  const renderCell = (row: number, col: number) => {
    const cell = gameState.grid[row][col];

    return (
      <div
        key={cell.id}
        className={getCellClassName(row, col)}
        onClick={() => handleCellClick(row, col)}
      >
        {cell.diceType && (
          <div className="dice-content">
            <div className="dice-emoji">{DICE_EMOJI[cell.diceType]}</div>
            <div className="dice-label">{cell.diceType}</div>
          </div>
        )}
      </div>
    );
  };

  const metaCurrencyEarned = calculateMetaCurrency(gameState.sparklesCreated);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/games/dice-match/menu" />
          </IonButtons>
          <IonTitle>Dice Match</IonTitle>
          {gameState.gameStatus === 'active' && (
            <IonButtons slot="end">
              <IonButton onClick={handleEndGame} color="light" strong={true}>
                End Game
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>

      <IonContent className="dice-match-content">
        {/* Loading State */}
        {!gameInitialized && (
          <div className="loading-overlay">
            <div className="loading-content">
              <div className="loading-spinner">🎲</div>
              <p>Loading game...</p>
            </div>
          </div>
        )}
        <div className="dice-match-container">
          {/* Stats Header */}
          <IonCard className="stats-card">
            <IonCardContent>
              <IonGrid>
                <IonRow>
                  <IonCol size="4">
                    <div className="stat">
                      <div className="stat-label">Score</div>
                      <div className="stat-value">{gameState.score}</div>
                    </div>
                  </IonCol>
                  <IonCol size="4">
                    <div className="stat">
                      <div className="stat-label">Slots</div>
                      <div className="stat-value">{gameState.diceSlots.length}</div>
                    </div>
                  </IonCol>
                  <IonCol size="4">
                    <div className="stat">
                      <div className="stat-label">💰</div>
                      <div className="stat-value">{gameState.inGameCurrency}</div>
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>

              {gameState.sparklesCreated > 0 && (
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                  <IonBadge color="warning">
                    ✨ {gameState.sparklesCreated} Sparkles Created
                  </IonBadge>
                </div>
              )}
            </IonCardContent>
          </IonCard>

          {/* Game Grid */}
          <div className="game-grid-container">
            <div className={`game-grid grid-size-${gameState.grid.length}`}>
              {gameState.grid.map((row, rowIndex) => (
                <div key={`row-${rowIndex}`} className="grid-row">
                  {row.map((_, colIndex) => renderCell(rowIndex, colIndex))}
                </div>
              ))}
            </div>
          </div>

          {/* Current Roll - Always visible to prevent layout jump */}
          <IonCard className="current-roll-card">
            <IonCardContent>
              <div className="roll-label">Available Dice - Tap to Select:</div>

              {/* Show all available dice as selectable options */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
                {gameState.availableDice.map((diceType, index) => (
                  <div
                    key={`die-${index}`}
                    onClick={() => setGameState({ ...gameState, currentRoll: diceType })}
                    style={{
                      padding: '12px 16px',
                      border: gameState.currentRoll === diceType ? '3px solid var(--ion-color-primary)' : '2px solid var(--ion-color-medium)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: gameState.currentRoll === diceType ? 'var(--ion-color-primary-tint)' : 'var(--ion-color-light)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      minWidth: '60px',
                      transition: 'all 0.2s',
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>{DICE_EMOJI[diceType]}</span>
                    <span style={{ fontSize: '12px', fontWeight: gameState.currentRoll === diceType ? 'bold' : 'normal' }}>
                      {diceType}
                    </span>
                  </div>
                ))}
              </div>

              {gameState.currentRoll && (
                <div className="roll-instruction" style={{ marginTop: '12px', textAlign: 'center' }}>
                  {gameState.trinketEffects.wildDieNext ? 'Tap a cell to choose die type' : `Selected: ${DICE_EMOJI[gameState.currentRoll]} ${gameState.currentRoll} - Tap a cell to place`}
                </div>
              )}

              {/* Trinket Action Buttons */}
              <div className="trinket-actions" style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                {/* Reroll Button */}
                {gameState.trinketEffects.rerollsRemaining > 0 && gameState.currentRoll && (
                  <IonButton
                    size="small"
                    fill="outline"
                    onClick={handleReroll}
                  >
                    <IonIcon icon={refresh} slot="start" />
                    Reroll ({gameState.trinketEffects.rerollsRemaining})
                  </IonButton>
                )}

                {/* Undo Button */}
                {gameState.trinketEffects.undoRemaining > 0 && gameState.moveHistory.length > 0 && (
                  <IonButton
                    size="small"
                    fill="outline"
                    onClick={handleUndo}
                  >
                    <IonIcon icon={arrowUndo} slot="start" />
                    Undo ({gameState.trinketEffects.undoRemaining})
                  </IonButton>
                )}
              </div>
            </IonCardContent>
          </IonCard>

          {/* Upgrades */}
          <IonCard className="upgrades-card">
            <IonCardContent>
              <h3>In-Game Upgrades</h3>
              <div className="upgrade-buttons">
                <IonButton
                  expand="block"
                  onClick={() => handlePurchaseUpgrade('slot')}
                  disabled={gameState.inGameCurrency < 100}
                >
                  {gameState.diceSlots.length >= 12
                    ? 'Upgrade Dice Slots (💰 100)'
                    : 'Add Dice Slot (💰 100)'}
                </IonButton>
              </div>
              {gameState.diceSlots.length >= 12 && (
                <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)', marginTop: '8px', marginBottom: 0 }}>
                  Max slots reached! Upgrades improve generation quality.
                </p>
              )}
            </IonCardContent>
          </IonCard>

          {/* Equipped Trinkets - Compact */}
          {equippedTrinkets.length > 0 && (
            <div className="trinkets-compact">
              {equippedTrinkets.map(trinket => {
                if (!trinket.template) return null;
                const isActive = activeTrinkets.has(trinket.trinket_id);
                const isPermanent = trinket.template.duration === 'permanent';
                const canActivate = !isActive && !isPermanent && gameState.gameStatus === 'active';

                return (
                  <div key={trinket.id} className={`trinket-compact ${isActive ? 'active' : ''} ${!canActivate && !isPermanent ? 'disabled' : ''}`}>
                    <div className="trinket-compact-icon">{trinket.template.icon}</div>
                    <div className="trinket-compact-name">{trinket.template.name}</div>
                    {canActivate && (
                      <IonButton
                        size="small"
                        color="primary"
                        onClick={() => handleActivateTrinket(trinket)}
                        style={{ fontSize: '13px', height: '28px' }}
                      >
                        Use
                      </IonButton>
                    )}
                    {isPermanent && (
                      <IonBadge color="success" style={{ fontSize: '12px' }}>Active</IonBadge>
                    )}
                    {isActive && !isPermanent && (
                      <IonBadge color="medium" style={{ fontSize: '12px' }}>Used</IonBadge>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Active Bonuses */}
          {(meta && (meta.upgrade_starting_dice > 0 || meta.upgrade_starting_grid > 0 || meta.upgrade_currency_multiplier > 0)) && (
            <IonCard className="bonuses-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <IonCardContent>
                <h4 style={{ marginTop: 0, marginBottom: '12px', color: 'white' }}>⚡ Active Upgrades</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
                  {meta.upgrade_starting_dice > 0 && (
                    <div>🎲 +{meta.upgrade_starting_dice} Starting Dice Slots</div>
                  )}
                  {meta.upgrade_starting_grid > 0 && (
                    <div>📐 5x5 Grid (25 spaces)</div>
                  )}
                  {meta.upgrade_currency_multiplier > 0 && (
                    <div>💰 +{meta.upgrade_currency_multiplier * 10}% Currency Bonus</div>
                  )}
                </div>
              </IonCardContent>
            </IonCard>
          )}

          {/* How to Play */}
          <IonCard className="info-card">
            <IonCardContent>
              <h4>How to Play</h4>
              <p>
                🎲 Merge matching dice to upgrade them<br />
                🔺 d4 → 🎲 d6 → 💎 d8 → 🔟 d10 → ⬡ d12 → 🌟 d20 → 💯 d100 → ✨ Sparkle
              </p>
              <p>
                🎰 Dice slots regenerate after each placement<br />
                📈 Higher slot numbers have better chances for higher dice<br />
                ✨ Sparkles lock the space and earn meta currency!<br />
                💰 Use currency to buy more dice slots
              </p>
            </IonCardContent>
          </IonCard>

          {/* Personal Best & Leaderboard */}
          {personalBest > 0 && (
            <IonCard>
              <IonCardContent>
                <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IonIcon icon={trophy} color="warning" />
                  Your Personal Best: {personalBest}
                </h3>
              </IonCardContent>
            </IonCard>
          )}

          {/* Leaderboard - Always visible */}
          <IonCard>
            <IonCardContent>
              <h3 style={{ marginTop: 0 }}>🏆 Leaderboard</h3>
              {isLoadingLeaderboard ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div className="loading-spinner" style={{
                    display: 'inline-block',
                    width: '24px',
                    height: '24px',
                    border: '3px solid var(--ion-color-medium)',
                    borderTop: '3px solid var(--ion-color-primary)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p style={{ marginTop: '12px', color: 'var(--ion-color-medium)', fontSize: '14px' }}>
                    Loading leaderboard...
                  </p>
                </div>
              ) : leaderboard.length > 0 ? (
                <div className="leaderboard-list">
                  {leaderboard.map((score, index) => (
                    <div key={score.id} className="leaderboard-entry">
                      <div className="leaderboard-rank">#{index + 1}</div>
                      <div className="leaderboard-name">
                        {score.display_name || score.username || 'Anonymous'}
                      </div>
                      <div className="leaderboard-score">{score.score}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: 'var(--ion-color-medium)', fontSize: '14px' }}>
                  No scores yet. Be the first to make the leaderboard!
                </p>
              )}
            </IonCardContent>
          </IonCard>
        </div>

        {/* Game Over Alert */}
        <IonAlert
          isOpen={showGameOver}
          onDidDismiss={() => setShowGameOver(false)}
          header="💀 GAME OVER"
          subHeader={isSavingScore ? 'Saving score...' : `Final Score: ${gameState.score}`}
          message={
            isSavingScore ? 'Please wait while we save your score...' :
            scoreSubmissionError ? `
⚠️ Failed to save score - please check your connection.
Your score has been saved locally and will be submitted automatically when connection is restored.

Sparkles Created: ${gameState.sparklesCreated}
Meta Currency Earned: ${metaCurrencyEarned}
${droppedTrinket ? `\n🎁 Trinket Dropped!\n${droppedTrinket.icon} ${droppedTrinket.name}` : ''}
          ` : `
Sparkles Created: ${gameState.sparklesCreated}
Meta Currency Earned: ${metaCurrencyEarned}
${droppedTrinket ? `\n🎁 Trinket Dropped!\n${droppedTrinket.icon} ${droppedTrinket.name}` : ''}
          `
          }
          buttons={
            isSavingScore ? [] : [
              {
                text: 'New Game',
                handler: handleNewGame,
              },
              {
                text: 'Exit',
                role: 'cancel',
                handler: () => {
                  window.history.back();
                },
              },
            ]
          }
        />

        {/* Upgrade Confirmation */}
        <IonAlert
          isOpen={showUpgradeAlert}
          onDidDismiss={() => setShowUpgradeAlert(false)}
          header="Purchase Upgrade"
          message="Add a new dice slot for 100 currency? Higher slot numbers have better chances for higher dice!"
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Purchase',
              handler: confirmPurchase,
            },
          ]}
        />

        {/* Wild Die Selector */}
        <IonAlert
          isOpen={showWildDieSelector}
          onDidDismiss={() => {
            setShowWildDieSelector(false);
            setPendingPlacement(null);
          }}
          header="🎭 Wild Die - Choose Type"
          message="Select which die type to place:"
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            ...['d4', 'd6', 'd8', 'd10', 'd12', 'd20', 'd100'].map(die => ({
              text: `${DICE_EMOJI[die as DiceType]} ${die}`,
              handler: () => handleWildDieSelect(die as DiceType),
            })),
          ]}
        />

        {/* End Game Confirmation */}
        <IonAlert
          isOpen={showEndGameConfirm}
          onDidDismiss={() => setShowEndGameConfirm(false)}
          header="End Game?"
          message="Are you sure you want to end this game? Your current score will be submitted."
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'End Game',
              handler: confirmEndGame,
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default DiceMatch;
